import { useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useMatches, useNavigate, useParams } from 'react-router-dom';

import { useStore } from '../context/store';
import { Credential } from '../models/credential';
import agent from '../api/agent';
import { Static } from '../static';
import { AppRouteHandle } from '../models/types';

const ClientLogin = () => {
	const { userStore, loaderStore } = useStore();
	const matches = useMatches();
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();

	const matchWithConfig = [...matches]
		.reverse()
		.find((match) => match.handle as AppRouteHandle | undefined);

	const handle = matchWithConfig?.handle as AppRouteHandle | undefined;

	const brand = handle?.brand;
	const notFoundPath = handle?.notFoundPath ?? '/not-found';
	const gonePath = handle?.gonePath ?? '/not-found';
	const trackingLabel = brand ? `Login ${brand}` : 'Login Unknown';

	const login = useCallback(
		async (loginId: string) => {
			try {
				loaderStore.setAppLoadingTrue();

				await userStore.authenticate(new Credential(loginId, null));

				const decodedToken = userStore.getDecodedToken();

				agent.Tracking.track(trackingLabel, decodedToken?.url);

				navigate(`${decodedToken?.url}/${decodedToken?.id}`);
			} catch (error: any) {
				const status = error?.response?.status;

				if (status === 410) {
					toast.error(Static.theDocumentIsNoLongerAvailable);
					navigate(gonePath);
				} else if (status === 401) {
					toast.error(Static.incorrectAuthenticationData);
					navigate(notFoundPath);
				} else {
					toast.error(Static.anUnexpectedErrorHasOccurred);
					navigate(notFoundPath);
				}
			} finally {
				loaderStore.setAppLoadingFalse();
			}
		},
		[loaderStore, userStore, trackingLabel, navigate, notFoundPath, gonePath],
	);

	useEffect(() => {
		if (!id) {
			navigate(notFoundPath);
			return;
		}

		login(id);
	}, [id, login, navigate, notFoundPath]);

	return null;
};

export default ClientLogin;
